import type { Point, Rect } from './camera';
import { obbBounds, obbContainsPoint, obbIntersectsRect } from './geometry';
import { toLocal } from './handles';
import type { PointerType } from './tools/tool';
import type { SceneObject, SpatialIndex } from './spatial';

/**
 * How deep inside its edge a section is taken (screen px, P5e): deeper, its empty inside belongs to the marquee (FigJam).
 * Capped at a quarter of its smaller side, so a small section keeps an inside too.
 */
export const SECTION_BAND_PX: Record<PointerType, number> = { mouse: 16, pen: 20, touch: 32 };

/**
 * Topmost selectable object under a world point; tolerance and a section's contour band are given in screen pixels
 * (`Infinity`: a section's whole inside counts). Locked objects are skipped unless asked.
 */
export function hitTest(
  index: SpatialIndex,
  objs: ReadonlyMap<string, SceneObject>,
  p: Point,
  tolerancePx: number,
  zoom: number,
  includeLocked = false,
  bandPx = SECTION_BAND_PX.mouse,
): string | null {
  const tol = tolerancePx / zoom;
  const ids = index.queryRect({ minX: p.x - tol, minY: p.y - tol, maxX: p.x + tol, maxY: p.y + tol });
  const hits = ids
    .map((id) => objs.get(id))
    .filter((o): o is SceneObject => !!o && (includeLocked || !o.locked) && contains(o, p, tol, zoom) && (o.type !== 'section' || inBand(o, p, bandPx / zoom)))
    .sort((a, b) => b.rank - a.rank || (a.z < b.z ? 1 : a.z > b.z ? -1 : a.id < b.id ? 1 : -1));
  return hits[0]?.id ?? null;
}

function nearPolyline(path: readonly Point[], p: Point, tol: number): boolean {
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!;
    const b = path[i]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
    if (Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy)) <= tol) return true;
  }
  return false;
}

/** Thin objects are hit within at least 6 screen px of their line. */
const contains = (o: SceneObject, p: Point, tol: number, zoom: number) => (o.path ? nearPolyline(o.path, p, Math.max(tol, 6 / zoom)) : obbContainsPoint(o.obb, p, tol));

/** Within `band` (world) inside the section's edge, or outside it (the tolerance already checked that). */
const inBand = (o: SceneObject, p: Point, band: number) => {
  if (band === Infinity) return true; // the whole section (targets: connector ends, comment pins)
  const { x, y } = toLocal(o.obb, p);
  const d = Math.min(o.obb.w / 2 - Math.abs(x), o.obb.h / 2 - Math.abs(y)); // depth inside (negative outside)
  return d < Math.min(band, Math.min(o.obb.w, o.obb.h) / 4);
};

/** Whether segment a–b touches `r` (Liang–Barsky clipping): a drawn line is taken where it crosses, not only at its points. */
function segmentHitsRect(a: Point, b: Point, r: Rect): boolean {
  let t0 = 0;
  let t1 = 1;
  // Keeps the part of the segment where p·t ≤ q; false once nothing is left.
  const clip = (p: number, q: number) => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) t0 = Math.max(t0, t);
    else t1 = Math.min(t1, t);
    return t0 <= t1;
  };
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return clip(-dx, a.x - r.minX) && clip(dx, r.maxX - a.x) && clip(-dy, a.y - r.minY) && clip(dy, r.maxY - a.y);
}

const pathTouches = (path: readonly Point[], r: Rect) =>
  path.length === 1 ? segmentHitsRect(path[0]!, path[0]!, r) : path.slice(1).some((b, i) => segmentHitsRect(path[i]!, b, r));

const inside = (o: SceneObject, r: Rect) => {
  const b = obbBounds(o.obb);
  return b.minX >= r.minX && b.minY >= r.minY && b.maxX <= r.maxX && b.maxY <= r.maxY;
};

/**
 * Selectable objects whose exact (rotated) shape intersects the marquee; locked objects are skipped, and a section is
 * taken only when it is entirely inside (dragging across a section selects its content, not the section).
 */
export function marqueeSelect(index: SpatialIndex, objs: ReadonlyMap<string, SceneObject>, r: Rect): string[] {
  return index
    .queryRect(r)
    .map((id) => objs.get(id))
    .filter((o): o is SceneObject => !!o && !o.locked && (o.type === 'section' ? inside(o, r) : o.path ? pathTouches(o.path, r) : obbIntersectsRect(o.obb, r)))
    .map((o) => o.id);
}
